'use client'

import { useState, useRef } from 'react'
import {
    MdContentCopy,
    MdCheck,
    MdSearch,
    MdExpandMore,
    MdExpandLess,
} from 'react-icons/md'

interface TranscriptionSegment {
    start: number
    end: number
    text: string
}

interface TranscriptionViewerProps {
    transcription: string
    segments?: TranscriptionSegment[]
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'
    transcribedAt?: string
    onSeek?: (time: number) => void
    className?: string
    maxHeight?: string
    showSearch?: boolean
    showTimestamps?: boolean
}

export function TranscriptionViewer({
    transcription,
    segments,
    status,
    transcribedAt,
    onSeek,
    className = '',
    maxHeight = '400px',
    showSearch = true,
    showTimestamps = true,
}: TranscriptionViewerProps) {
    const [copied, setCopied] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [isExpanded, setIsExpanded] = useState(true)
    const contentRef = useRef<HTMLDivElement>(null)

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(transcription)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy:', err)
        }
    }

    const highlightText = (text: string): React.ReactNode => {
        if (!searchTerm.trim()) return text

        const regex = new RegExp(`(${searchTerm})`, 'gi')
        const parts = text.split(regex)

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} className="bg-warning/40 rounded px-0.5">
                    {part}
                </mark>
            ) : (
                part
            )
        )
    }

    if (status === 'PENDING') {
        return (
            <div className={`bg-base-200 rounded-xl p-6 ${className}`}>
                <div className="flex flex-col items-center justify-center gap-3 text-base-content/60">
                    <svg
                        className="w-12 h-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                    </svg>
                    <p className="text-sm">Aguardando audio para transcrição</p>
                </div>
            </div>
        )
    }

    if (status === 'PROCESSING') {
        return (
            <div className={`bg-base-200 rounded-xl p-6 ${className}`}>
                <div className="flex flex-col items-center justify-center gap-3">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                    <p className="text-sm text-base-content/60">
                        Transcrevendo audio...
                    </p>
                    <p className="text-xs text-base-content/40">
                        Isso pode levar alguns minutos
                    </p>
                </div>
            </div>
        )
    }

    if (status === 'FAILED') {
        return (
            <div className={`bg-base-200 rounded-xl p-6 ${className}`}>
                <div className="flex flex-col items-center justify-center gap-3 text-error">
                    <svg
                        className="w-12 h-12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <p className="text-sm">Falha na transcrição</p>
                    <p className="text-xs text-base-content/40">
                        Tente novamente ou entre em contato com o suporte
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className={`bg-base-200 rounded-xl overflow-hidden ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="btn btn-ghost btn-sm btn-square"
                    >
                        {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                    </button>
                    <div>
                        <h3 className="font-semibold text-base-content">
                            Transcrição
                        </h3>
                        {transcribedAt && (
                            <p className="text-xs text-base-content/60">
                                Transcrito em{' '}
                                {new Date(transcribedAt).toLocaleString(
                                    'pt-BR'
                                )}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch && (
                        <div className="relative">
                            <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-sm input-bordered pl-9 w-40"
                            />
                        </div>
                    )}
                    <button
                        onClick={copyToClipboard}
                        className="btn btn-ghost btn-sm gap-1"
                        title="Copiar transcrição"
                    >
                        {copied ? (
                            <>
                                <MdCheck className="text-success" />
                                <span className="text-xs">Copiado</span>
                            </>
                        ) : (
                            <>
                                <MdContentCopy />
                                <span className="text-xs">Copiar</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Content */}
            {isExpanded && (
                <div
                    ref={contentRef}
                    className="p-4 overflow-y-auto"
                    style={{ maxHeight }}
                >
                    {segments && segments.length > 0 && showTimestamps ? (
                        <div className="space-y-3">
                            {segments.map((segment, index) => (
                                <div
                                    key={index}
                                    className="flex gap-3 hover:bg-base-300/50 rounded-lg p-2 -m-2 transition-colors"
                                >
                                    <button
                                        onClick={() => onSeek?.(segment.start)}
                                        className="text-xs font-mono text-secondary hover:underline shrink-0 pt-0.5"
                                        disabled={!onSeek}
                                    >
                                        {formatTime(segment.start)}
                                    </button>
                                    <p className="text-sm text-base-content leading-relaxed">
                                        {highlightText(segment.text)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap">
                            {highlightText(transcription)}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}
